import { type CSSProperties, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { usePostContentContext } from '@/postContentElement/context';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

import { appendProtocol, isAnchorLink, isMailToLink, isTelLink } from '../../utils/link';

import * as styles from './InlineLink.css';
import type { LeafMarkProps } from './Text.types';

export function InlineLinkElement(props: LeafMarkProps) {
  const { mark, children, className, isIndexingDisabled = false } = props;

  const { attrs } = mark;

  const target = attrs?.target !== '_self' ? '_blank' : undefined;
  const isClickTrackingDisabled =
    attrs?.clicktracking === 'off' ||
    isAnchorLink(attrs?.href || '') ||
    isMailToLink(attrs?.href || '') ||
    isTelLink(attrs?.href || '');

  const additionalAttributes = {};
  if (isClickTrackingDisabled) {
    additionalAttributes['data-skip-tracking'] = 'true';
  }

  const replacedRel = useMemo(() => {
    try {
      if (typeof attrs?.rel === 'string') {
        return attrs.rel
          .replace(/nofollow|noreferrer|noopener/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }
    } catch {
      return attrs?.rel;
    }
  }, [attrs?.rel]);
  const rel = isIndexingDisabled ? attrs?.rel : replacedRel;

  const { utmParams } = usePostContentContext();
  const [hrefWithUtmParams, setHrefWithUtmParams] = useState(attrs?.href);
  // make sure to update the href *after* hydration to avoid mismatches
  useEffect(() => {
    setHrefWithUtmParams(evaluateAndAppendUTMParams(appendProtocol(attrs?.href, 'http://'), utmParams));
  }, [attrs?.href, utmParams]);

  const style = useMemo(() => {
    const color = attrs?.color;
    if (!color) {
      return {} as CSSProperties;
    }

    return {
      '--wt-inline-link-underline-color-override': color,
      '--wt-inline-link-color-override': color,
    } as CSSProperties;
  }, [attrs?.color]);

  return (
    <a
      href={hrefWithUtmParams}
      target={target}
      rel={rel}
      className={classNames(className, styles.linkElement)}
      style={style}
      {...additionalAttributes}
    >
      {children}
    </a>
  );
}
